// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ApiHandler, createHandlerChainRunner } from "@insite/client-framework/HandlerCreator";
import {
    BusinessAddress,
    validateAddress as sendValidateAddress,
    ValidateAddressApiParameter,
    ValidateAddressResult,
} from "@mcmc/App/Services/AccountCreationService";

function generateAddressIdAndLabel(index: number, address: BusinessAddress) {
    let addressItems = [
        address.address1,
        address.city,
        address.state + (address.postalCode && address.state ? ` ${address.postalCode}` : `${address.postalCode}`),
    ];
    addressItems = addressItems.filter(item => !!item);
    address.id = `${index}${addressItems.join("-")}`;
    address.label = `${addressItems.join(", ")}`;
}

function trimAddress1(address: BusinessAddress) {
    address.address1 = address.address1.substr(0, 30);
}

export interface ValidateAddressParameter extends ValidateAddressApiParameter {
    onError?: (error: any) => void;
    onSuccess?: (results: ValidateAddressResult) => void;
}

type HandlerType = ApiHandler<ValidateAddressParameter, ValidateAddressResult>;

export const PopulateApiParameter: HandlerType = props => {
    props.apiParameter = { ...props.parameter };
    delete props.apiParameter.onError;
    delete props.apiParameter.onSuccess;
};

export const RequestDataFromApi: HandlerType = async props => {
    try {
        const result = await sendValidateAddress(props.apiParameter);
        if (result.isValid) {
            if (result.normalizedAddresses && result.normalizedAddresses.length > 0) {
                result.normalizedAddresses.every((address, index) => {
                    trimAddress1(address);
                    generateAddressIdAndLabel(index, address);
                    return true;
                });
            } else {
                const address: BusinessAddress = { ...props.apiParameter };
                trimAddress1(address);
                generateAddressIdAndLabel(0, address);
                result.normalizedAddresses = [address];
            }
        }
        props.parameter.onSuccess?.(result);
    } catch (error) {
        props.parameter.onError?.(
            error && ((error.errorJson && error.errorJson.message) || error.errorMessage || error),
        );
        return false;
    }
};

export const chain = [PopulateApiParameter, RequestDataFromApi];

const validateAddress = createHandlerChainRunner(chain, "ValidateAddress");
export default validateAddress;
